import { useCallback, useEffect, useState } from "preact/hooks";
import { db, guessLocalZone } from "./tz";
import { resolveZone } from "./tz/aliases";
import { parseIsoDate, parseTime, pad2, type TimeFormat } from "./tz/civil";

export interface AppState {
  zone: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  fmt: TimeFormat;
  /** Output zones shown below the map (canonical ids, in click order). */
  watch: string[];
}

const MAX_WATCH = 24;

function parseWatch(param: string | null): string[] {
  const out: string[] = [];
  for (const part of (param ?? "").split(",")) {
    const id = resolveZone(part);
    if (id && !out.includes(id)) out.push(id);
    if (out.length >= MAX_WATCH) break;
  }
  return out;
}

const FMT_KEY = "timezoo.fmt";
const DETECT_KEY = "timezoo.detect";

export type DetectConsent = "yes" | "no" | null;

/** Whether the user allowed reading the device's time zone (null = not asked yet). */
export function getDetectConsent(): DetectConsent {
  try {
    const v = localStorage.getItem(DETECT_KEY);
    if (v === "yes" || v === "no") return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function setDetectConsent(v: Exclude<DetectConsent, null>): void {
  try {
    localStorage.setItem(DETECT_KEY, v);
  } catch {
    /* ignore */
  }
}

/** True when the page was opened with a zone in the URL (a shared link). */
export const openedWithZone = resolveZone(new URLSearchParams(location.hash.replace(/^#/, "")).get("z") ?? "") !== null;

function todayInZone(zone: string): { year: number; month: number; day: number; hour: number; minute: number } {
  const now = Math.floor(Date.now() / 1000);
  const local = now + db.zone(zone).offsetAt(now);
  const d = new Date(local * 1000);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() };
}

function readFmt(): TimeFormat {
  try {
    const v = localStorage.getItem(FMT_KEY);
    if (v === "12h" || v === "24h") return v;
  } catch {
    /* ignore */
  }
  // Follow the browser locale's preference.
  try {
    const parts = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).formatToParts(new Date());
    return parts.some((p) => p.type === "dayPeriod") ? "12h" : "24h";
  } catch {
    return "24h";
  }
}

function fromHash(): AppState {
  const params = new URLSearchParams(location.hash.replace(/^#/, ""));
  const zoneParam = params.get("z") ?? "";
  // Only read the device's time zone when the user has allowed it; it can
  // reveal where they are (think of someone streaming their screen).
  const zone = resolveZone(zoneParam) ?? (getDetectConsent() === "yes" ? guessLocalZone() : "UTC");
  const now = todayInZone(zone);
  const date = parseIsoDate(params.get("d") ?? "");
  const time = parseTime(params.get("t") ?? "");
  const fmtParam = params.get("f");
  const fmt: TimeFormat = fmtParam === "12" || fmtParam === "12h" ? "12h" : fmtParam === "24" || fmtParam === "24h" ? "24h" : readFmt();
  return {
    zone,
    year: date?.year ?? now.year,
    month: date?.month ?? now.month,
    day: date?.day ?? now.day,
    hour: time?.hour ?? now.hour,
    minute: time?.minute ?? now.minute,
    fmt,
    watch: parseWatch(params.get("w")),
  };
}

function toHash(s: AppState): string {
  const p = new URLSearchParams();
  p.set("z", s.zone);
  p.set("d", `${s.year}-${pad2(s.month)}-${pad2(s.day)}`);
  p.set("t", `${pad2(s.hour)}:${pad2(s.minute)}`);
  if (s.watch.length) p.set("w", s.watch.join(","));
  return "#" + p.toString().replace(/%2F/g, "/").replace(/%3A/g, ":").replace(/%2C/g, ",");
}

export function useAppState(): [AppState, (patch: Partial<AppState>) => void] {
  const [state, setState] = useState<AppState>(fromHash);

  useEffect(() => {
    const onHash = () => setState((prev) => ({ ...fromHash(), fmt: prev.fmt }));
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const h = toHash(state);
    if (location.hash !== h) history.replaceState(null, "", h);
    try {
      localStorage.setItem(FMT_KEY, state.fmt);
    } catch {
      /* ignore */
    }
  }, [state]);

  const update = useCallback((patch: Partial<AppState>) => setState((prev) => ({ ...prev, ...patch })), []);
  return [state, update];
}

export { todayInZone };
